import { Plus, Folders } from 'lucide-react'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { TopicFormDialog, type TopicFormData } from './TopicFormDialog'
import { TopicListItem, type Topic } from './TopicListItem'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'

interface TopicsInbox {
  id: string
  name: string
}

interface TopicsData {
  topics: Topic[]
  inboxes: TopicsInbox[]
}

interface TopicsSettingsCardProps {
  channel: {
    id: string
    config: {
      require_topic: boolean
    }
  } | null
  topics: TopicsData | null
}

export function TopicsSettingsCard({ channel, topics: topicsData }: TopicsSettingsCardProps) {
  const { withAccount } = useRouteParams()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null)
  const [requireTopic, setRequireTopic] = useState(channel?.config?.require_topic ?? false)
  const [formData, setFormData] = useState<TopicFormData>({
    name: '',
    icon: '',
    description: '',
    inbox_id: '',
    active: true,
    channel_ids: []
  })

  const accountParams = withAccount()

  if (!topicsData) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Unable to load topics data.</p>
        </CardContent>
      </Card>
    )
  }

  const { inboxes } = topicsData
  // Topics belong to the widget you're configuring — only show this widget's topics.
  const topics = channel ? topicsData.topics.filter((t) => t.channel_ids.includes(channel.id)) : topicsData.topics

  const handleRequireTopicChange = (checked: boolean) => {
    setRequireTopic(checked)
    router.patch(routes.settings.updateWidget.path(withAccount()), {
      widget: { require_topic: checked },
      channel_id: channel?.id,
      ...activeBrandParam(),
    }, { preserveScroll: true })
  }

  const openCreateDialog = () => {
    setEditingTopic(null)
    setFormData({
      name: '',
      icon: '',
      description: '',
      inbox_id: inboxes[0]?.id || '',
      active: true,
      // A topic added here belongs to the widget being configured.
      channel_ids: channel ? [channel.id] : []
    })
    setDialogOpen(true)
  }

  const openEditDialog = (topic: Topic) => {
    setEditingTopic(topic)
    setFormData({
      name: topic.name,
      icon: topic.icon || '',
      description: topic.description || '',
      inbox_id: topic.inbox_id,
      active: topic.active,
      channel_ids: topic.channel_ids ?? []
    })
    setDialogOpen(true)
  }

  const handleSubmit = () => {
    if (!formData.name.trim() || !formData.inbox_id) {
      return
    }

    const topicData = {
      topic: {
        name: formData.name.trim(),
        icon: formData.icon || null,
        description: formData.description.trim() || null,
        inbox_id: formData.inbox_id,
        active: formData.active,
        channel_ids: formData.channel_ids
      },
      channel_id: channel?.id,
      ...activeBrandParam(),
    }

    if (editingTopic) {
      router.patch(
        `/${accountParams.account_id}/topics/${editingTopic.id}`,
        topicData,
        {
          preserveScroll: true,
          onSuccess: () => setDialogOpen(false),
        }
      )
    } else {
      router.post(
        `/${accountParams.account_id}/topics`,
        topicData,
        {
          preserveScroll: true,
          onSuccess: () => setDialogOpen(false),
        }
      )
    }
  }

  const handleDelete = (topicId: string) => {
    router.delete(`/${accountParams.account_id}/topics/${topicId}`, {
      data: { channel_id: channel?.id, ...activeBrandParam() },
      preserveScroll: true,
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Folders className="size-5" />
              Conversation Topics
            </CardTitle>
            <CardDescription>
              Create topics that visitors can select to route conversations to the right inbox
            </CardDescription>
          </div>
          <Button onClick={openCreateDialog} size="sm" className="cursor-pointer">
            <Plus className="size-4 mr-1" />
            Add Topic
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {topics.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No topics yet. Create your first topic to help route conversations.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topics.map((topic) => (
              <TopicListItem
                key={topic.id}
                topic={topic}
                onEdit={openEditDialog}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {topics.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border">
            <Checkbox
              id="require_topic"
              label="Require topic selection"
              checked={requireTopic}
              onCheckedChange={(checked) => handleRequireTopicChange(checked === true)}
            />
            <p className="text-sm text-muted-foreground ml-6 mt-1">
              When disabled, visitors can skip topic selection and start a general inquiry.
            </p>
          </div>
        )}
      </CardContent>

      <TopicFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        formData={formData}
        onFormChange={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingTopic}
        inboxes={inboxes}
      />
    </Card>
  )
}
